const TTL_MS = 10 * 60_000;
const STORAGE_PREFIX = "te-search-list:";
const STORAGE_MAX_ITEMS = 150;

export interface ListSnapshot<T> {
  items: T[];
  page: number;
  hasMore: boolean;
  seen: number[];
  at: number;
}

const STORE = new Map<string, ListSnapshot<unknown>>();

function storage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readStored<T>(key: string): ListSnapshot<T> | null {
  const box = storage();
  if (!box) return null;

  try {
    const raw = box.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as ListSnapshot<T>;

    if (!Array.isArray(parsed?.items) || Date.now() - parsed.at > TTL_MS) {
      box.removeItem(STORAGE_PREFIX + key);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeStored<T>(key: string, snapshot: ListSnapshot<T>): void {
  const box = storage();
  if (!box) return;

  if (snapshot.items.length > STORAGE_MAX_ITEMS) return;

  try {
    box.setItem(STORAGE_PREFIX + key, JSON.stringify(snapshot));
  } catch {
    return;
  }
}

export function readList<T>(key: string): ListSnapshot<T> | null {
  const saved = (STORE.get(key) as ListSnapshot<T> | undefined) ?? readStored<T>(key);

  if (!saved) return null;

  if (Date.now() - saved.at > TTL_MS) {
    STORE.delete(key);
    return null;
  }

  STORE.set(key, saved as ListSnapshot<unknown>);

  return saved;
}

export function writeList<T>(key: string, snapshot: Omit<ListSnapshot<T>, "at">): void {
  const stamped = { ...snapshot, at: Date.now() } as ListSnapshot<T>;

  STORE.set(key, stamped as ListSnapshot<unknown>);
  writeStored(key, stamped);
}

export function dropListState(): void {
  STORE.clear();

  const box = storage();
  if (!box) return;

  try {
    for (const key of Object.keys(box)) {
      if (key.startsWith(STORAGE_PREFIX)) box.removeItem(key);
    }
  } catch {
    return;
  }
}
